/**
 * Auto-fill for related single-choice fields (CU-869dz0un3, extended for the
 * supplier catalog).
 *
 * When the builder marks single-choice options with `autoFill`, the related
 * ones form a group. The first time a value is picked in one of them, the
 * still-empty siblings are filled with the choice that carries the same label.
 * Fields that already hold a value are never overwritten, so the visitor can
 * freely choose a different value per field afterwards. Multi-choice is out of
 * scope.
 *
 * Two flagged options are RELATED when they draw from the same product set
 * (`choice_set_id`) or share at least one choice label. Relatedness is
 * transitive within a product, so "kleur kap", "kleur geleider" and "kleur
 * onderlijst" become one group even if the onderlijst has fewer colours. Page
 * and channel do not matter: a group spans all pages of the product and
 * includes options that are hidden in the current channel (`internalOnly` on
 * the website, `externalOnly` in the measuring form). Hidden siblings are
 * seeded too, so their price deltas count exactly as if they had been shown.
 *
 * Price is deliberately NOT part of the identity: seeding is label-based (each
 * sibling keeps its own choice id, and therefore its own price). Per-choice
 * SKU/supplier codes are ignored too, since the same palette often carries
 * field-specific codes.
 */

const normalizeLabel = (label) =>
  String(label ?? '')
    .trim()
    .toLowerCase();

const isEmptyValue = (v) => v === undefined || v === null || v === '';

const labelSet = (choices = []) =>
  new Set((choices || []).map((c) => normalizeLabel(c?.label)).filter((l) => l !== ''));

/**
 * Whether two single-choice options can fill each other: same product set, or
 * at least one shared choice label.
 */
export function areAutoFillRelated(a, b) {
  if (!a || !b || a === b) return false;
  if (a.choice_set_id && b.choice_set_id && a.choice_set_id === b.choice_set_id) return true;
  const labelsA = labelSet(a.choiceConfig?.choices);
  const labelsB = labelSet(b.choiceConfig?.choices);
  if (!labelsA.size || !labelsB.size) return false;
  for (const label of labelsA) {
    if (labelsB.has(label)) return true;
  }
  return false;
}

/**
 * Cluster the single-choice options of ONE product by relatedness
 * (transitive). Returns option id → ids of its cluster (incl. itself), so the
 * builder can show eligibility and flip a whole cluster together. Pass
 * `flaggedOnly: true` to cluster only the options with `autoFill: true`, which
 * is what the runtime seeds.
 *
 * @param {Array} options - raw product options
 * @param {{ flaggedOnly?: boolean }} [opts]
 * @returns {Object<string, string[]>}
 */
export function clusterAutoFillOptions(options = [], { flaggedOnly = false } = {}) {
  const candidates = (options || []).filter(
    (o) =>
      o &&
      o.id &&
      o.type === 'single-choice' &&
      (!flaggedOnly || o.autoFill === true) &&
      (o.choiceConfig?.choices?.length || o.choice_set_id)
  );

  const parent = {};
  const find = (id) => {
    while (parent[id] !== id) {
      parent[id] = parent[parent[id]];
      id = parent[id];
    }
    return id;
  };
  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  candidates.forEach((o) => {
    parent[o.id] = o.id;
  });
  for (let i = 0; i < candidates.length; i += 1) {
    for (let j = i + 1; j < candidates.length; j += 1) {
      if (areAutoFillRelated(candidates[i], candidates[j]))
        union(candidates[i].id, candidates[j].id);
    }
  }

  const byRoot = {};
  candidates.forEach((o) => {
    const root = find(o.id);
    (byRoot[root] = byRoot[root] || []).push(o.id);
  });
  const byId = {};
  Object.values(byRoot).forEach((ids) => {
    ids.forEach((id) => {
      byId[id] = ids;
    });
  });
  return byId;
}

/**
 * Build the auto-fill groups straight from a product definition, independent
 * of which options are rendered. Field ids follow the runtime convention
 * `${productId}-${optionId}-input`, so the groups apply to formData directly.
 *
 * @param {{ id: string, options?: Array }} product
 * @returns {AutoFillGroup[]}
 */
export function buildProductAutoFillGroups(product) {
  if (!product?.id) return [];
  const clusters = clusterAutoFillOptions(product.options || [], { flaggedOnly: true });
  const seen = new Set();
  const groups = [];
  Object.values(clusters).forEach((ids) => {
    if (ids.length < 2 || seen.has(ids)) return;
    seen.add(ids);
    groups.push({
      key: `${product.id}::${ids.join('|')}`,
      fieldIds: ids.map((optionId) => `${product.id}-${optionId}-input`),
    });
  });
  return groups;
}

/**
 * Choice lists keyed by field id for every single-choice option of a product,
 * hidden ones included, so a seeded sibling can be resolved by label even when
 * it is not on any rendered page.
 *
 * @param {{ id: string, options?: Array }} product
 * @returns {Object<string, Array>}
 */
export function productChoicesByFieldId(product) {
  const map = {};
  if (!product?.id) return map;
  (product.options || []).forEach((option) => {
    if (!option?.id || option.type !== 'single-choice') return;
    const choices = option.choiceConfig?.choices;
    if (!Array.isArray(choices)) return;
    map[`${product.id}-${option.id}-input`] = choices.map((choice, index) => ({
      ...choice,
      id: choice.id || `${option.id}-choice-${index}`,
    }));
  });
  return map;
}

/**
 * Order- and count-sensitive signature of a choice list over its labels only.
 * Two options with the same signature draw from the same palette, so they are
 * eligible to auto-fill each other. Price is intentionally excluded (see file
 * header). A plain array is already order-stable, so no key-sorting is needed.
 *
 * @param {Array} choices - raw builder choices
 * @returns {string}
 */
export function choiceSignature(choices = []) {
  return JSON.stringify((choices || []).map((c) => normalizeLabel(c?.label)));
}

/**
 * @typedef {Object} AutoFillGroup
 * @property {string} key - stable id (`${pageId}::${signature}`)
 * @property {string[]} fieldIds - formData field ids in the group (≥2)
 */

/**
 * Build the auto-fill groups for the given funnel pages: within each page, the
 * single-choice elements flagged `autoFill` that share a signature, ≥2 members.
 *
 * @param {Array<{ id: string, elements?: Array }>} pages
 * @returns {AutoFillGroup[]}
 */
export function buildAutoFillGroups(pages = []) {
  const groups = [];
  (pages || []).forEach((page) => {
    if (!page) return;
    const bySignature = {};
    (page.elements || []).forEach((el) => {
      const p = (el && el.props) || {};
      if (p.autoFill !== true) return;
      if ((p.selectionMode || 'single') !== 'single') return;
      const sig = p.autoFillSignature || '';
      (bySignature[sig] = bySignature[sig] || []).push(el.id);
    });
    Object.keys(bySignature).forEach((sig) => {
      const fieldIds = bySignature[sig];
      if (fieldIds.length >= 2) {
        groups.push({ key: `${page.id}::${sig}`, fieldIds });
      }
    });
  });
  return groups;
}

/**
 * Seed a picked value into the still-empty siblings of its auto-fill group.
 * Returns an empty object when nothing should be filled.
 *
 * @param {Object} params
 * @param {string} params.changedFieldId
 * @param {*} params.value - the picked choice id (single-choice string)
 * @param {AutoFillGroup} params.group
 * @param {Object<string, Array>} params.choicesById - field id → its choice list
 * @param {Object<string, *>} params.currentValues - current formData (to detect empties)
 * @param {Set<string>} [params.seededFieldIds] - fields whose current value was
 *   itself auto-filled; those follow a new pick instead of being left behind.
 *   Essential for siblings hidden in this channel, which nobody can correct.
 * @returns {Object<string, string>} siblingFieldId → choice id
 */
export function resolveAutoFillPatch({
  changedFieldId,
  value,
  group,
  choicesById = {},
  currentValues = {},
  seededFieldIds = new Set(),
}) {
  const patch = {};
  if (!group || typeof value !== 'string' || value === '') return patch;

  const picked = (choicesById[changedFieldId] || []).find((c) => c.id === value);
  if (!picked) return patch;
  const targetLabel = normalizeLabel(picked.label);

  group.fieldIds.forEach((fieldId) => {
    if (fieldId === changedFieldId) return;
    const choices = choicesById[fieldId];
    if (!choices) return;
    // Seed a still-empty sibling, one that still only holds its default, or one
    // that was seeded earlier — never overwrite a value the visitor actively chose.
    const current = currentValues[fieldId];
    if (!isEmptyValue(current) && !seededFieldIds.has(fieldId)) {
      const defaultId = choices.find((c) => c.isDefault)?.id;
      if (current !== defaultId) return;
    }
    const match = choices.find((c) => normalizeLabel(c.label) === targetLabel);
    if (match) patch[fieldId] = match.id;
  });

  return patch;
}
